import React from 'react';

import MemberCourseCell from './MemberCourseCell';
import { useMyBucketList } from '../../hooks/useMemberBucketListApi';
import { MemberBucketListSummaryDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { renderBooleanBadge } from '../../utils/displayUtils';
import Table from '../common/Table';

interface BucketListTableProps {
  // Narrows the rows by course name. Lower-cased and trimmed by the caller.
  search?: string;
  // Pads the hint in from the edges, for a home with no padding of its own (the golf lists). A
  // Card already pads its content.
  inset?: boolean;
}

// The member's bucket list, not-yet-played first. Read only: a course goes on and off the list
// from the Explore map. One component, two homes: the desktop Bucket List page, and the Bucket
// list tab of the profile's golf lists (/member/profile/golf), the phone's way in.
const BucketListTable: React.FC<BucketListTableProps> = ({
  search = '',
  inset = false,
}) => {
  const { data: bucketList, isLoading } = useMyBucketList();

  const rows = (bucketList ?? []).filter(
    (row) =>
      search.length === 0 ||
      (row.courseName ?? '').toLowerCase().includes(search),
  );

  return (
    <>
      <p
        className={`pb-3 text-sm text-ink-muted dark:text-white/60 ${
          inset ? 'px-4 pt-3' : ''
        }`}
      >
        Courses you&apos;d like to play, not-yet-played first. Add one from the
        map.
      </p>

      <Table
        columns={[
          {
            label: 'Course',
            key: 'courseName',
            className: '!px-2 sm:!px-4',
            render: (row: MemberBucketListSummaryDTO) => (
              <MemberCourseCell
                courseId={row.courseId}
                name={row.courseName}
                subtitle={row.courseAddress}
                logoFileId={row.courseLogoFileId}
                closed={row.courseClosed}
              />
            ),
          },
          {
            label: 'Status',
            key: 'completed',
            className: '!px-2 sm:!px-4',
            render: (row: MemberBucketListSummaryDTO) =>
              renderBooleanBadge(
                row.completed ?? false,
                'Played',
                'Not Played',
                'brand',
              ),
          },
          {
            // Off on a phone, where the course and its status need the width. The status already
            // says whether it has been played.
            label: 'Played On',
            key: 'completedUtc',
            className: 'hidden sm:table-cell',
            format: (value: Date) =>
              value ? convertToUserTime(value.toString(), true) : '-',
          },
        ]}
        data={rows}
        isLoading={isLoading}
        allowPaging={false}
        expandableDetails={false}
        noDataMessage={
          search.length > 0
            ? 'No course matches that.'
            : 'Your bucket list is empty.'
        }
      />
    </>
  );
};

export default BucketListTable;
